'use client';

import { Permission } from '@up2stay/up2promo-api-client';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@up2stay/up2promo-ui';
import { ChevronDown, Library, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { FeedbackFormsPanel } from '@/core/components/feedback/feedback-forms-panel';
import { FeedbackMessagesPanel } from '@/core/components/feedback/feedback-messages-panel';
import { FeedbackPageBreadcrumb } from '@/core/components/feedback/feedback-page-breadcrumb';
import { FeedbackPageShell } from '@/core/components/feedback/feedback-page-shell';
import { AnimatedUnderlineTabs } from '@/core/components/ui/tabs-29';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';

export type FeedbackLibraryTab = 'forms' | 'messages';

function parseTab(value: string | null): FeedbackLibraryTab {
  return value === 'messages' ? 'messages' : 'forms';
}

export function FeedbackLibraryPage() {
  const dict = useDictionary();
  const feedbackDict = dict.feedback;
  const { navigateShell } = useNavigateShell();
  const { can } = usePermissions();
  const canEdit = can(Permission.FEEDBACK_INVITE);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeTab = parseTab(searchParams.get('tab'));
  const [libraryOpen, setLibraryOpen] = useState(false);

  const setTab = (tab: FeedbackLibraryTab) => {
    setLibraryOpen(false);
    const params = new URLSearchParams(location.search);
    if (tab === 'forms') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    const query = params.toString();
    navigateShell(
      query
        ? `${shellPaths.feedbackLibrary}?${query}`
        : shellPaths.feedbackLibrary,
      { intent: 'updateQuery' },
    );
  };

  const createBlankHref =
    activeTab === 'messages'
      ? shellPaths.feedbackMessageNew
      : shellPaths.feedbackFormNew;
  const fromLibraryLabel =
    activeTab === 'messages'
      ? feedbackDict.messages.fromLibrary
      : feedbackDict.forms.fromLibrary;
  const createBlankLabel =
    activeTab === 'messages'
      ? feedbackDict.messages.createBlank
      : feedbackDict.forms.createBlank;

  const tabs = useMemo(
    () => [
      {
        name: feedbackDict.library.tabs.forms,
        value: 'forms',
        content: (
          <FeedbackFormsPanel
            showInlineCreate
            libraryOpen={activeTab === 'forms' ? libraryOpen : false}
            onLibraryOpenChange={setLibraryOpen}
          />
        ),
      },
      {
        name: feedbackDict.library.tabs.messages,
        value: 'messages',
        content: (
          <FeedbackMessagesPanel
            showInlineCreate
            libraryOpen={activeTab === 'messages' ? libraryOpen : false}
            onLibraryOpenChange={setLibraryOpen}
          />
        ),
      },
    ],
    [
      activeTab,
      feedbackDict.library.tabs.forms,
      feedbackDict.library.tabs.messages,
      libraryOpen,
    ],
  );

  return (
    <FeedbackPageShell
      title={feedbackDict.library.title}
      breadcrumb={
        <FeedbackPageBreadcrumb
          domainLabel={dict.nav.navDomainWork}
          pageLabel={dict.nav.feedbackLibrary}
        />
      }
      actionsLayout="stack"
      actions={
        canEdit ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" size="sm">
                <Plus className="size-4" />
                {feedbackDict.library.add}
                <ChevronDown className="size-3.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setLibraryOpen(true)}>
                <Library className="size-4" />
                {fromLibraryLabel}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigateShell(createBlankHref)}>
                <Plus className="size-4" />
                {createBlankLabel}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null
      }
    >
      <AnimatedUnderlineTabs
        value={activeTab}
        onValueChange={(value) => setTab(parseTab(value))}
        className="w-full"
        tabs={tabs}
      />
    </FeedbackPageShell>
  );
}
